// Thinking.jsx
import React, { useRef, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./ProblemDescription.css";
import { MdKeyboardArrowUp } from "react-icons/md";
import { FiMic, FiStopCircle } from "react-icons/fi";
import { useGlobal } from "../../context/GlobalContext";
import { useAuraVoice } from "../../hooks/useAuraVoice";
import Stars from "../../assets/stars.svg";
import Logo from "../../assets/logo.svg";
import Mic from "../../assets/mic.svg";
import Stop from "../../assets/stop.svg";
import { sendUserAudio } from "../../services/api"; // <-- import your function

const MAX_HEIGHT = 150;

const Thinking = ({ value, onChange }) => {
  const [error, setError] = useState("");
  const [recStatus, setRecStatus] = useState("idle"); // idle | recording | uploading
  const [hint, setHint] = useState("");               // small UX hint text

  const navigate = useNavigate();
  const { dispatch } = useGlobal();
  useAuraVoice("Então... me conta uma coisa. Qual o maior desafio que você tá enfrentando no seu trabalho hoje?", "question");

  const taRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const validateInput = (v) => v.trim().length > 0;

  const resize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = next + "px";
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  };

  useLayoutEffect(() => {
    resize();
  }, [value]);

  const handleSubmit = () => {
    if (validateInput(value)) {
      setError("");
      dispatch({ type: "UPDATE_STATE", payload: { userContext: value } });
      navigate("/processing");
    } else {
      setError("Por favor, descreva seu desafio.");
    }
  };

  // --- MIC LOGIC -------------------------------------------------------------

  const getMimeType = () => {
    if (MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus")) {
      return "audio/webm;codecs=opus";
    }
    if (MediaRecorder.isTypeSupported?.("audio/webm")) {
      return "audio/webm";
    }
    // Safari support for MediaRecorder is still evolving; we’ll still try webm.
    return "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = handleStop;
      mr.start();

      mediaRecorderRef.current = mr;
      setRecStatus("recording");
      setHint("Gravando… clique para parar");
    } catch (err) {
      console.error(err);
      setHint("Não foi possível acessar o microfone.");
    }
  };

  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.stop();
      mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    } catch {}
    setRecStatus("uploading");
    setHint("Enviando áudio…");
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];

    try {
      // If your backend expects a specific field name (e.g. "file"), keep it consistent:
      const file = new File([blob], "mic.webm", { type: "audio/webm" });
      const form = new FormData();
      
      form.append("audio", file, "mic.webm");   // <-- MUST be "audio"
      form.append("language", "pt"); 

      for (const [k, v] of form.entries()) {
        console.log("FD:", k, v instanceof File ? `${v.name} (${v.type}) size=${v.size}` : v);
      }

      const result = await sendUserAudio(form);

      // If API returns just the transcript string:
      const transcript = typeof result === "string"
        ? result
        : (result?.transcript || result?.text || "");

      if (transcript) {
        onChange(transcript); // push it into the textarea
        setHint("Transcrição recebida.");
      } else {
        setHint("Áudio enviado, mas não recebi texto.");
      }
    } catch (err) {
      console.error(err);
      setHint("Falha ao enviar/interpretar o áudio.");
    } finally {
      setRecStatus("idle");
      // Let the hint fade after a moment
      setTimeout(() => setHint(""), 2500);
    }
  };

  const toggleMic = () => {
    if (recStatus === "recording") {
      stopRecording();
    } else if (recStatus === "idle") {
      startRecording();
    }
  };

  // --------------------------------------------------------------------------

  return (
    <div className="thinking-container">
      <img src={Stars} alt="Estrelas no céu" className="stars-background" />
      <main className="thinking-content">
        <motion.img 
          src={Logo} 
          alt="Logo" 
          className="thinking-logo"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.8, 
            ease: "easeOut" 
          }}
        />
        <motion.h1 
          className="thinking-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: "easeOut", 
            delay: 1.0 
          }}
        >
          Qual o maior desafio que você enfrenta no seu trabalho hoje?
        </motion.h1>

        <div className="input-container">
          <textarea
            ref={taRef}
            placeholder="Conte sobre o desafio"
            value={value}
            onChange={(e) => { if (error) setError(""); onChange(e.target.value); }}
            onInput={resize}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            rows={1}
            aria-label="Descrição do desafio"
          />
          <div className="input-icons">
            {/* MIC BUTTON */}
            <button
              type="button"
              className={`mic-btn ${recStatus}`}
              onClick={toggleMic}
              aria-label={recStatus === "recording" ? "Parar gravação" : "Gravar áudio"}
              disabled={recStatus === "uploading"}
              title={recStatus === "recording" ? "Parar" : "Gravar"}
            >
              {recStatus === "recording" ? <img src={Stop} className="mic-icon"/> : <img src={Mic} className="mic-icon"/>}
            </button>

            {/* SUBMIT ARROW */}
            <div className="arrow-icon-container" onClick={handleSubmit}>
              <MdKeyboardArrowUp className="arrow-icon" />
            </div>
          </div>
        </div>

        {/* Tiny status hint under input */}
        {(hint || error) && (
          <p className={error ? "error-message" : "hint-message"}>
            {error || hint}
          </p>
        )}
      </main>
    </div>
  );
};

export default Thinking;
